import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { RitualStep, RitualState, Block, UnfinishedDecision } from '@/types';
import { getRandomStoicQuote, StoicQuote } from '@/constants/stoicQuotes';
import { RevisionSummary } from '@/hooks/useRevisionLog';
import { ReviewStep } from './ritual/ReviewStep';
import { RevisionSummaryStep } from './ritual/RevisionSummaryStep';
import { UnfinishedStep } from './ritual/UnfinishedStep';
import { CaptureStep } from './ritual/CaptureStep';
import { PlanTomorrowStep } from './ritual/PlanTomorrowStep';
import { ReflectionStep } from './ritual/ReflectionStep';
import { QuoteStep } from './ritual/QuoteStep';
import { CelebrationScreen } from './ritual/CelebrationScreen';
import styles from './ShutdownRitual.module.css';

const STEPS: RitualStep[] = [
  'review',
  'revision-summary',
  'unfinished',
  'capture',
  'plan-tomorrow',
  'reflection',
  'quote',
];

const STEP_LABELS: Record<RitualStep, string> = {
  review: 'Review Today',
  'revision-summary': 'Schedule Revisions',
  unfinished: 'Handle Unfinished',
  capture: 'Capture Tasks',
  'plan-tomorrow': 'Plan Tomorrow',
  reflection: 'Daily Reflection',
  quote: 'Daily Affirmation',
};

interface ShutdownRitualProps {
  todayBlocks: Block[];
  revisionSummary: RevisionSummary;
  onComplete: (state: RitualState) => void;
  onSkip: () => void;
  onViewRevisionHistory?: () => void;
}

export function ShutdownRitual({
  todayBlocks,
  revisionSummary,
  onComplete,
  onSkip,
  onViewRevisionHistory,
}: ShutdownRitualProps) {
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [completedSteps, setCompletedSteps] = useState<RitualStep[]>([]);
  const [direction, setDirection] = useState<1 | -1>(1);
  const [showCelebration, setShowCelebration] = useState(false);
  const [celebrationComplete, setCelebrationComplete] = useState(false);

  // Ritual state
  const [reviewData, setReviewData] = useState({
    completedBlocks: todayBlocks.filter((b) => b.status === 'completed').map((b) => b.id),
    missedBlocks: todayBlocks.filter((b) => b.status === 'planned').map((b) => b.id),
  });
  const [unfinishedDecisions, setUnfinishedDecisions] = useState<
    Record<string, UnfinishedDecision>
  >({});
  const [capturedTasks, setCapturedTasks] = useState<string[]>([]);
  const [tomorrowBlocks, setTomorrowBlocks] = useState<Block[]>([]);
  const [reflection, setReflection] = useState('');
  const [quote] = useState<StoicQuote>(getRandomStoicQuote());

  const currentStep = STEPS[currentStepIndex];
  const progress = ((currentStepIndex + 1) / STEPS.length) * 100;

  // Update review data when blocks change
  useEffect(() => {
    setReviewData({
      completedBlocks: todayBlocks.filter((b) => b.status === 'completed').map((b) => b.id),
      missedBlocks: todayBlocks.filter((b) => b.status === 'planned').map((b) => b.id),
    });
  }, [todayBlocks]);

  const handleNext = useCallback(() => {
    if (currentStepIndex < STEPS.length - 1) {
      setDirection(1);
      setCompletedSteps((prev) => [...prev, currentStep]);
      setCurrentStepIndex((prev) => prev + 1);
    } else {
      // Complete the ritual
      const finalState: RitualState = {
        currentStep: 'quote',
        completedSteps: [...completedSteps, 'quote'],
        reviewData,
        unfinishedDecisions,
        capturedTasks,
        tomorrowBlocks,
        reflection,
        quote,
      };
      setShowCelebration(true);

      // Show celebration for 3 seconds then allow completion
      setTimeout(() => {
        setCelebrationComplete(true);
      }, 3000);

      onComplete(finalState);
    }
  }, [
    currentStepIndex,
    currentStep,
    completedSteps,
    reviewData,
    unfinishedDecisions,
    capturedTasks,
    tomorrowBlocks,
    reflection,
    quote,
    onComplete,
  ]);

  const handleBack = useCallback(() => {
    if (currentStepIndex > 0) {
      setDirection(-1);
      setCompletedSteps((prev) => prev.filter((s) => s !== STEPS[currentStepIndex - 1]));
      setCurrentStepIndex((prev) => prev - 1);
    }
  }, [currentStepIndex]);

  const handleUnfinishedDecision = useCallback((blockId: string, decision: UnfinishedDecision) => {
    setUnfinishedDecisions((prev) => ({ ...prev, [blockId]: decision }));
  }, []);

  const handleAddCapturedTask = useCallback((task: string) => {
    setCapturedTasks((prev) => [...prev, task]);
  }, []);

  const handleRemoveCapturedTask = useCallback((index: number) => {
    setCapturedTasks((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const handleAddTomorrowBlock = useCallback(
    (block: Omit<Block, 'id' | 'userId' | 'date' | 'createdAt' | 'updatedAt'>) => {
      const newBlock: Block = {
        ...block,
        id: `temp-${Date.now()}`,
        userId: '',
        date: '',
        createdAt: {} as any,
        updatedAt: {} as any,
      };
      setTomorrowBlocks((prev) => [...prev, newBlock]);
    },
    []
  );

  const handleRemoveTomorrowBlock = useCallback((blockId: string) => {
    setTomorrowBlocks((prev) => prev.filter((b) => b.id !== blockId));
  }, []);

  const unfinishedBlocks = useMemo(
    () => todayBlocks.filter((b) => b.status === 'planned'),
    [todayBlocks]
  );

  const completedBlocks = useMemo(
    () => todayBlocks.filter((b) => b.status === 'completed'),
    [todayBlocks]
  );

  const carryOverBlocks = useMemo(
    () => unfinishedBlocks.filter((b) => unfinishedDecisions[b.id] === 'carry-over'),
    [unfinishedBlocks, unfinishedDecisions]
  );

  const isLastStep = currentStepIndex === STEPS.length - 1;
  const isFirstStep = currentStepIndex === 0;

  // Animation variants
  const slideVariants = {
    enter: (direction: number) => ({
      x: direction > 0 ? 300 : -300,
      opacity: 0,
    }),
    center: {
      x: 0,
      opacity: 1,
    },
    exit: (direction: number) => ({
      x: direction > 0 ? -300 : 300,
      opacity: 0,
    }),
  };

  if (showCelebration) {
    return <CelebrationScreen onComplete={onSkip} completed={celebrationComplete} />;
  }

  return (
    <div
      className={styles.container}
      role="dialog"
      aria-modal="true"
      aria-label="Shutdown ritual wizard"
    >
      {/* Progress Bar */}
      <div
        className={styles.progressBar}
        role="progressbar"
        aria-valuenow={progress}
        aria-valuemin={0}
        aria-valuemax={100}
      >
        <motion.div
          className={styles.progressFill}
          initial={{ width: 0 }}
          animate={{ width: `${progress}%` }}
          transition={{ type: 'spring', stiffness: 100, damping: 15 }}
        />
      </div>

      {/* Header with Step Indicator */}
      <header className={styles.header}>
        <div className={styles.stepDots} role="navigation" aria-label="Ritual steps">
          {STEPS.map((step, index) => {
            const isCompleted = completedSteps.includes(step);
            const isCurrent = index === currentStepIndex;
            return (
              <motion.div
                key={step}
                className={`${styles.stepDot} ${isCompleted ? styles.stepDotCompleted : ''} ${isCurrent ? styles.stepDotCurrent : ''}`}
                initial={{ scale: 1 }}
                animate={{ scale: isCurrent ? 1.2 : 1 }}
                transition={{ type: 'spring', stiffness: 300, damping: 20 }}
                aria-label={`Step ${index + 1}: ${STEP_LABELS[step]}${isCompleted ? ' (completed)' : isCurrent ? ' (current)' : ''}`}
              />
            );
          })}
        </div>
        <p className={styles.stepLabel}>
          Step {currentStepIndex + 1} of {STEPS.length} - {STEP_LABELS[currentStep]}
        </p>
      </header>

      {/* Step Content */}
      <main className={styles.contentContainer}>
        <AnimatePresence mode="wait" custom={direction}>
          <motion.div
            key={currentStep}
            className={styles.stepWrapper}
            custom={direction}
            variants={slideVariants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{ type: 'spring', stiffness: 300, damping: 30 }}
          >
            {currentStep === 'review' && (
              <ReviewStep
                completedBlocks={completedBlocks}
                plannedBlocks={unfinishedBlocks}
                totalBlocks={todayBlocks}
              />
            )}
            {currentStep === 'revision-summary' && (
              <RevisionSummaryStep
                summary={revisionSummary}
                onViewFullHistory={onViewRevisionHistory}
              />
            )}
            {currentStep === 'unfinished' && (
              <UnfinishedStep
                unfinishedBlocks={unfinishedBlocks}
                decisions={unfinishedDecisions}
                onDecision={handleUnfinishedDecision}
              />
            )}
            {currentStep === 'capture' && (
              <CaptureStep
                capturedTasks={capturedTasks}
                onAddTask={handleAddCapturedTask}
                onRemoveTask={handleRemoveCapturedTask}
              />
            )}
            {currentStep === 'plan-tomorrow' && (
              <PlanTomorrowStep
                blocks={tomorrowBlocks}
                onAddBlock={handleAddTomorrowBlock}
                onRemoveBlock={handleRemoveTomorrowBlock}
                carryOverBlocks={carryOverBlocks}
              />
            )}
            {currentStep === 'reflection' && (
              <ReflectionStep reflection={reflection} onReflectionChange={setReflection} />
            )}
            {currentStep === 'quote' && (
              <QuoteStep
                quote={quote}
                summaryData={{
                  blocksReviewed: todayBlocks.length,
                  blocksCompleted: completedBlocks.length,
                  unfinishedHandled: Object.keys(unfinishedDecisions).length,
                  tasksCaptured: capturedTasks.length,
                  tomorrowBlocksPlanned: tomorrowBlocks.length + carryOverBlocks.length,
                  hasReflection: reflection.length > 0,
                }}
              />
            )}
          </motion.div>
        </AnimatePresence>
      </main>

      {/* Navigation Footer */}
      <footer className={styles.footer}>
        <motion.button
          className={`${styles.navButton} ${styles.backButton}`}
          onClick={handleBack}
          disabled={isFirstStep}
          whileHover={{ scale: isFirstStep ? 1 : 1.02 }}
          whileTap={{ scale: isFirstStep ? 1 : 0.98 }}
          aria-label="Go to previous step"
        >
          Back
        </motion.button>

        <button className={styles.skipLink} onClick={onSkip} aria-label="Skip shutdown ritual">
          Skip for now
        </button>

        <motion.button
          className={`${styles.navButton} ${styles.nextButton}`}
          onClick={handleNext}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          aria-label={isLastStep ? 'Complete shutdown ritual' : 'Go to next step'}
        >
          {isLastStep ? 'Complete Shutdown' : 'Next'}
        </motion.button>
      </footer>
    </div>
  );
}

export default ShutdownRitual;
